import { defineStore } from "pinia";
import { Chat, ChatMessage } from "@/types/chat";
import chatService from "@/services/chatService";
import { getCookie } from "@/use/useCookie";
import { TP_COOKIE_NAME } from "@/env_config";
import decodeJWT from "jwt-decode";
import { filterByBoard, filterByChats } from "@/use/useMessages";
import { requestAndShowPermission } from "@/use/useBrowserNotification";

const APP = "APP";

interface ChatStoreState {
  chats: Chat[];
  isChatActive: boolean;
  activeChat: Chat | null;
  chatUsername: string;
  isChatHubActive: boolean;
}

export const useChatStore = defineStore("chat", {
  state: (): ChatStoreState => ({
    chats: [],
    isChatActive: false,
    activeChat: null,
    chatUsername: "",
    isChatHubActive: false
  }),
  actions: {
    toggleChatHubMode(state: boolean) {
      this.isChatHubActive = state;
    },
    async fetchChats(boardIds: string[]) {
      try {
        const { data } = await chatService.getChats(boardIds);
        this.chats = data.chats;
      } catch (e) {
        // hide all error messages
      }
    },
    async markAllMessagesRead() {
      const localChats = [...this.chats];
      const bookingIds: string[] = [];
      localChats.forEach(chat => {
        if (chat.unreadCount > 0) {
          bookingIds.push(chat.bookingId);
        }
      });
      if (bookingIds.length > 0) {
        try {
          await chatService.readAllChatMessages(bookingIds);
          this.chats.forEach(chat => (chat.unreadCount = 0));
        } catch (e) {
          console.error("could not finish the request", e);
        }
      }
    },
    async fetchMessagesForActiveChat(bookingId: string) {
      try {
        this.setActiveChat(null);
        const { data } = await chatService.readChatMessages(bookingId);
        this.setActiveChat(data);
      } catch (e) {
        console.error("could not fetch chat messages", e);
      }
    },
    initChatUsername() {
      const cookieValue = getCookie(TP_COOKIE_NAME);
      if (cookieValue) {
        const decodedJWT: any = decodeJWT(cookieValue);
        this.chatUsername = decodedJWT.sub;
      }
    },
    chatUpdate(incomingChat: Chat) {
      const localChats = [...this.chats];

      const outdatedChatIndex = localChats.findIndex(chat => chat.bookingId === incomingChat.bookingId);

      const activeConversation = this.activeChat?.messages || [];

      if (filterByBoard(incomingChat)) {
        //CHAT_UPDATE message board included in the working board/s
        const chatFoundOnBoard = filterByChats(localChats, incomingChat);
        if (chatFoundOnBoard) {
          //CHAT_UPDATE message - Chat bookingId Found in Chats on board
          if (this.activeChat && this.activeChat.bookingId === incomingChat.bookingId) {
            this.activeChat.messages =
              incomingChat.messages && incomingChat.messages.length > 0
                ? [...new Set([...incomingChat.messages, ...activeConversation])]
                : this.activeChat.messages;
          }
          this.chats = localChats;
          if (incomingChat.messages && !this.activeChat) {
            chatFoundOnBoard.unreadCount += incomingChat.messages ? incomingChat.messages.length : 0;
            localChats.splice(outdatedChatIndex, 1, { ...chatFoundOnBoard, ...incomingChat });
            this.notifyUser(incomingChat);
          }
        } else {
          //CHAT_UPDATE message - Leg bookignId found/not found on the board Legs
          incomingChat.unreadCount = incomingChat.messages ? incomingChat.messages.length : 0;
          const updatedChats = [...localChats, ...[incomingChat]];
          this.chats = updatedChats;
          this.notifyUser(incomingChat);
        }
      }
    },
    chatRead(chatToClean: Chat) {
      this.chats.forEach(chat => {
        if (chat.bookingId === chatToClean.bookingId) {
          chat.unreadCount = 0;
        }
      });
    },
    notifyUser(updatedChat: Chat) {
      const messages: ChatMessage[] = updatedChat.messages || [];

      const latestMessageSequence = messages.length >= 1 ? Math.max(...messages.map(o => o.sequence)) : 0;

      const latestMessageIndex = messages.findIndex(object => {
        return object.sequence === latestMessageSequence;
      });

      const originIsAPP = messages[latestMessageIndex].origin === APP;

      if (originIsAPP) {
        requestAndShowPermission(updatedChat, messages[latestMessageIndex].author, messages[latestMessageIndex].text);
      }
    },
    setActiveChat(chat: Chat | null) {
      this.activeChat = chat;
      if (this.activeChat && this.activeChat.bookingId) {
        this.activeChat.unreadCount = 0;
      }
    }
  }
});
